import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  getAuthToken,
  handleUnauthorizedError,
  callLogoutApi,
  getValidRefreshedToken,
} from "@/utils/authUtils";
import { toast } from "@/components/toastify";
import { isQuizImagePathError } from "@/pages/quiz/utils/questionImage";

const baseQueries = {
  default: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_DEV_API_URL,
    prepareHeaders: (headers) => {
      const token = getAuthToken();
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
        headers.set(
          "X-Time-Zone",
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        );
      }
      return headers;
    },
  }),
  hrReport: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_IIY_API_URL,
    prepareHeaders: (headers) => {
      const token = getAuthToken();
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
        headers.set(
          "X-Time-Zone",
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        );
      }
      return headers;
    },
  }),
};

export const baseQueryWithInterceptor = async (
  args,
  api,
  extraOptions = {},
) => {
  try {
    const baseQuery =
      extraOptions?.sourceType === "HR_REPORT"
        ? baseQueries.hrReport
        : baseQueries.default;

    if (extraOptions?.sourceType === "THIRD_PARTY") {
      toast("success", "Fetching Data");
    }

    const isMultipart = args.body instanceof FormData;
    const modifiedArgs = {
      ...args,
      headers: {
        ...(args.headers || {}),
        ...(isMultipart ? {} : { "Content-Type": "application/json" }),
      },
    };

    const result = await baseQuery(modifiedArgs, api, extraOptions);

    // Parse blob error response if needed
    if (
      result.error?.data instanceof Blob &&
      result.error.data.type === "application/json"
    ) {
      try {
        result.error.data = JSON.parse(await result.error.data.text());
      } catch (parseErr) {
        console.error("Failed to parse error blob:", parseErr);
      }
    }

    // Handle errors
    if (result.error) {
      const { status, data } = result.error;
      const message = data?.message || "Something went wrong!";
      const suppressErrorToast = Boolean(extraOptions?.suppressErrorToast);
      const suppressNotFoundToast = Boolean(
        extraOptions?.suppressNotFoundToast,
      );

      if (suppressErrorToast) {
        return result;
      }

      // Access token likely expired: try to refresh using the (single-use) refresh
      // token, then retry the original request once with the new token.
      if (status === 401) {
        const newToken = await getValidRefreshedToken();
        if (newToken) {
          // prepareHeaders reads the token fresh, so the retry picks up the new one.
          const retryResult = await baseQuery(modifiedArgs, api, extraOptions);
          if (!retryResult.error || retryResult.error.status !== 401) {
            return retryResult;
          }
        }
        if (api?.endpoint === "googleLogin") {
          toast("error", "Google login failed.");
          await new Promise((resolve) => setTimeout(resolve, 3000));

          await callLogoutApi();
          handleUnauthorizedError();
          return;
        } else {
          await callLogoutApi();
          handleUnauthorizedError();
          return result;
        }
      }

      switch (status) {
        case 403:
        case 404:
          if (!(suppressNotFoundToast && status === 404)) {
            toast("error", message, `${status}`);
          }
          break;

        case 400:
        case 409:
          if (!isQuizImagePathError(message)) {
            toast("error", message, `${status}`);
          } else {
            console.warn("Quiz image path rejected by server:", message);
          }
          break;

        case 500:
          toast(
            "error",
            "Internal Server Error. Please try again later.",
            "500",
          );
          console.error("500 Server Error:", result.error);
          break;

        case 502:
          toast(
            "error",
            "Bad Gateway. Please check the server connection.",
            "502",
          );
          console.error("502 Bad Gateway:", result.error);
          break;

        case 503:
          toast("error", "Service Unavailable. Try again later.", "503");
          console.error("503 Service Unavailable:", result.error);
          break;

        default:
          if (data?.errorType === "OPERATIONAL") {
            toast("error", message, `${status}`);
          }
          console.error("API Error:", result.error);
      }
    }

    return result;
  } catch (error) {
    console.error("Interceptor error:", error);
    return {
      error: {
        status: "CUSTOM_ERROR",
        error: error.toString(),
        data: { message: "Network/Interceptor error occurred" },
      },
    };
  }
};
